import React, { useEffect, useRef, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import {
  MdDescription,
  MdInventory2,
  MdLocalShipping,
  MdArticle,
} from "react-icons/md";

import CircularLoader from "../../../../../../Components/CircularLoader/CircularLoader";

import Logo from "../../../../../../Components/logo-dark.png";
import Tooltip from "../../../../../../Components/Tooltip/Tooltip";
import {
  downloadShipmentFile,
  downloadMultipleFiles,
  fetchShipmentFiles,
} from "./labelDownloads";
import FileMenu from "./FileMenu";
import { asFile, releaseFiles } from "./fileActions";
import { useTranslation } from "react-i18next";

// "attention / partially fulfilled" → "Attention / Partially Fulfilled".
const titleCase = (name) =>
  String(name ?? "").replace(/(^|[\s/])(\w)/g, (match) => match.toUpperCase());

// `status` (optional): { options, value, onChange, disabled, hint } — given
// for users who can fulfil the order, the fulfilment status becomes a select.
// `getPdf` (optional): getPdf(orderId, packingList) → { blob, name }. With
// it, Order / Pack / Label / Decl ask what to do with the file (print,
// open, share to another app, download) instead of downloading it.
export default function OrderViewHeader({
  order,
  handleClose,
  downloadPdf,
  getPdf,
  status,
}) {
  const { t } = useTranslation(["warehouse", "common"]);
  const [busy, setBusy] = useState(null);
  // The file menu open (its button's label), and each file's state:
  // { [label]: { state: "loading" | "ready" | "error", files } }.
  const [menu, setMenu] = useState(null);
  const [prepared, setPrepared] = useState({});
  const jobs = useRef(new Map());
  const made = useRef([]);
  const actionsRef = useRef(null);

  const shipment = order?.shipment;
  const manual = order?.manual_shipment;

  // Another order, or this one changed (its status is on the PDF): the files
  // are got again when next asked for.
  useEffect(() => {
    jobs.current = new Map();
    setPrepared({});
    setMenu(null);
  }, [order]);
  useEffect(() => () => releaseFiles(made.current), []);

  // Downloads are async (PDF generation, file fetches) — hold the button in a
  // loading state until the work actually settles.
  const run = async (label, action) => {
    if (busy) return;
    setBusy(label);
    try {
      await action();
    } catch (error) {
      console.error(`${label} download failed:`, error);
    } finally {
      setBusy(null);
    }
  };

  const isCancelled = shipment?.shipment_status?.toLowerCase() === "cancelled";

  const pdfFiles = (packingList) => async () => {
    const { blob, name } = await getPdf(order?.order_id, packingList);
    return [asFile(blob, name)];
  };

  const actions = [
    {
      label: t("orderView.order"),
      icon: MdDescription,
      onClick: () => downloadPdf(order?.order_id, false),
      files: getPdf ? pdfFiles(false) : null,
      title: "Order PDF",
      disabled: false,
      tooltip: getPdf
        ? "Order PDF: print, open, share or download"
        : t("orderView.downloadOrderPdf"),
    },
    {
      label: t("orderView.pack"),
      icon: MdInventory2,
      onClick: () => downloadPdf(order?.order_id, true),
      files: getPdf ? pdfFiles(true) : null,
      title: "Packing list",
      disabled: false,
      tooltip: getPdf
        ? "Packing list: print, open, share or download"
        : t("orderView.downloadPackingListPdf"),
    },
    {
      label: t("orderView.label"),
      icon: MdLocalShipping,
      onClick: () =>
        manual?.shipping_labels?.length > 0
          ? downloadMultipleFiles(
              manual?.shipping_labels,
              manual?.shipment_tracking_id,
            )
          : downloadShipmentFile(shipment, "Label"),
      files: getPdf
        ? () =>
            fetchShipmentFiles({
              shipment,
              manualFiles: manual?.shipping_labels,
              trackingId: manual?.shipment_tracking_id,
              type: "Label",
            })
        : null,
      title: "Shipping label",
      disabled: !(
        (shipment?.single_shipping_labels?.file && !isCancelled) ||
        manual?.shipping_labels?.length > 0
      ),
      tooltip: getPdf
        ? "Shipping label: print, open, share or download"
        : t("orderView.downloadShippingLabel"),
    },
    {
      label: t("orderView.decl"),
      icon: MdArticle,
      onClick: () =>
        manual?.shipping_declarations?.length > 0
          ? downloadMultipleFiles(
              manual?.shipping_declarations,
              manual?.shipment_tracking_id,
            )
          : downloadShipmentFile(shipment, "Declaration"),
      files: getPdf
        ? () =>
            fetchShipmentFiles({
              shipment,
              manualFiles: manual?.shipping_declarations,
              trackingId: manual?.shipment_tracking_id,
              type: "Declaration",
            })
        : null,
      title: "Declaration",
      disabled: !(
        (shipment?.single_shipping_invoice?.file && !isCancelled) ||
        manual?.shipping_declarations?.length > 0
      ),
      tooltip: getPdf
        ? "Declaration: print, open, share or download"
        : t("orderView.downloadShipmentDeclaration"),
    },
  ];

  // Gets the button's file(s) ready, once per order (kept for the next tap).
  const prepare = (action) => {
    if (jobs.current.has(action.label)) return;
    const current = jobs.current;
    const job = Promise.resolve()
      .then(action.files)
      .then((files) => {
        if (!files?.length) throw new Error("No file to show");
        return files;
      });
    current.set(action.label, job);
    setPrepared((state) => ({
      ...state,
      [action.label]: { state: "loading" },
    }));
    job.then(
      (files) => {
        made.current.push(...files);
        if (jobs.current !== current) return;
        setPrepared((state) => ({
          ...state,
          [action.label]: { state: "ready", files },
        }));
      },
      (error) => {
        console.error(`${action.label} file failed:`, error);
        if (jobs.current !== current) return;
        current.delete(action.label);
        setPrepared((state) => ({
          ...state,
          [action.label]: { state: "error" },
        }));
      },
    );
  };

  const press = (action) => {
    if (!action.files) {
      run(action.label, action.onClick);
      return;
    }
    if (menu === action.label) {
      setMenu(null);
      return;
    }
    setMenu(action.label);
    prepare(action);
  };
  const openAction = actions.find((action) => action.label === menu);

  const fulfilmentStatus = order?.fullfilment_status?.name;
  const shipmentStatus = shipment?.shipment_status || manual?.shipment_status;
  const needsAttention =
    /attention|partial|pending|unfulfilled|hold|exception/i.test(
      `${fulfilmentStatus ?? ""} ${shipmentStatus ?? ""}`,
    );

  // Phones: logo, order number and Close on the first line, the status on
  // the next, then the downloads as one full-width row of 40px buttons with
  // their labels. From sm up: one line, as before (40px tall until lg).
  return (
    <header className="relative flex flex-wrap items-center gap-x-3 gap-y-2 bg-ui-inverted px-3 py-2 sm:px-4">
      <img
        src={Logo}
        alt="Hajex Bolt"
        className="h-[15px] w-auto shrink-0 object-contain"
      />

      <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-brand-contrast sm:max-w-none sm:flex-[0_1_auto] sm:text-[15px]">
        {t("orderView.order")} {order?.order_id ?? "-"}
      </span>

      {order?.id && (
        <span className="hidden max-w-[120px] truncate font-mono text-[11px] text-brand-contrast opacity-70 sm:inline">
          {order.id}
        </span>
      )}

      {(fulfilmentStatus || shipmentStatus || status) && (
        <span className="order-[2] flex min-w-0 basis-full items-center gap-1.5 text-[12px] text-brand-contrast sm:order-none sm:basis-auto">
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
              needsAttention ? "bg-state-warning-dot" : "bg-state-success-dot"
            }`}
          />
          {status ? (
            // Who can fulfil the order changes its status right here: the
            // order modal's status pop-up opens, as from the order list.
            <Tooltip title={status.hint || "Change the fulfilment status"}>
              <span className="min-w-0">
                <select
                  aria-label="Fulfilment status"
                  value={status.value ?? ""}
                  disabled={status.disabled}
                  onChange={(event) => status.onChange(event.target.value)}
                  className="min-h-[40px] w-full min-w-0 max-w-[240px] cursor-pointer truncate rounded-md border border-glass-border bg-transparent py-1 pl-2 pr-1 text-[13px] font-semibold text-brand-contrast outline-none transition-colors hover:bg-glass-chip-hover focus:border-brand-contrast disabled:cursor-not-allowed disabled:opacity-70 lg:min-h-0 lg:py-0.5 lg:text-[12px] [@media(pointer:coarse)]:min-h-[40px]"
                >
                  {status.value == null && (
                    <option value="" className="bg-ui-surface text-ui-text">
                      Fulfilment status
                    </option>
                  )}
                  {status.options.map((option) => (
                    <option
                      key={option.id}
                      value={option.id}
                      disabled={Boolean(option?.disabled)}
                      className="bg-ui-surface text-ui-text"
                    >
                      {titleCase(option.name)}
                    </option>
                  ))}
                </select>
              </span>
            </Tooltip>
          ) : (
            fulfilmentStatus && (
              <span className="truncate">{fulfilmentStatus}</span>
            )
          )}
          {shipmentStatus && (
            <span className="shrink-0 truncate">
              {fulfilmentStatus || status ? "· " : ""}
              {shipmentStatus}
            </span>
          )}
        </span>
      )}

      {/* No box of its own on phones: the downloads and Close place
          themselves in the header's rows. */}
      <div className="contents sm:ml-auto sm:flex sm:shrink-0 sm:items-center sm:gap-3">
        <div
          ref={actionsRef}
          className="order-[3] flex basis-full overflow-hidden rounded-md border border-glass-border sm:order-none sm:basis-auto"
        >
          {actions.map((action, index) => {
            const { label, icon: Icon, disabled, tooltip } = action;
            const isBusy =
              busy === label ||
              (menu === label && prepared[label]?.state === "loading");
            return (
              <Tooltip
                key={label}
                // None over its own open menu.
                title={disabled ? t("orderView.notAvailable") : menu === label ? "" : tooltip}
              >
                <button
                  type="button"
                  onClick={() => press(action)}
                  disabled={disabled || Boolean(busy)}
                  aria-label={tooltip}
                  {...(action.files
                    ? {
                        "aria-haspopup": "menu",
                        "aria-expanded": menu === label,
                      }
                    : {})}
                  className={`flex min-h-[40px] flex-1 items-center justify-center gap-1.5 px-[8px] py-1.5 text-[12px] font-medium text-brand-contrast transition-colors sm:flex-[0_1_auto] sm:justify-start sm:px-[12px] lg:min-h-0 [@media(pointer:coarse)]:min-h-[40px] ${
                    index < actions.length - 1
                      ? "border-r border-glass-border"
                      : ""
                  } ${
                    disabled
                      ? "cursor-not-allowed opacity-40"
                      : busy
                        ? "cursor-wait opacity-60"
                        : menu === label
                          ? "bg-glass-chip-hover opacity-100"
                          : "opacity-90 hover:bg-glass-chip-hover hover:opacity-100"
                  }`}
                >
                  {isBusy ? <CircularLoader size={12} /> : <Icon size={14} />}
                  <span>{label}</span>
                </button>
              </Tooltip>
            );
          })}
        </div>

        <button
          type="button"
          onClick={handleClose}
          title={t("common:actions.close")}
          className="order-[1] flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-brand-contrast opacity-70 transition-colors hover:bg-glass-chip-hover hover:opacity-100 sm:order-none lg:h-7 lg:w-7 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10"
        >
          <RxCross2 size={18} />
        </button>
      </div>

      {openAction && (
        <FileMenu
          key={openAction.label}
          title={openAction.title}
          status={prepared[openAction.label]}
          onRetry={() => prepare(openAction)}
          onClose={() => setMenu(null)}
          ignoreRef={actionsRef}
        />
      )}
    </header>
  );
}
